import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { X, KeyRound, Eye, EyeOff } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import CountrySelect, { COUNTRIES } from './CountrySelect';
import { requestOtp, verifyOtp } from '../../lib/api';
import TelegramOtpStep from './TelegramOtpStep';

export default function ForgotPasswordModal({ open, onOpenChange }) {
  const { data } = useSite();
  const { refresh } = useAuth();
  const [step, setStep] = useState(1); // 1 mobile, 2 new pwd, 3 otp
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [mobile, setMobile] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [otpInfo, setOtpInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => { setStep(1); setMobile(''); setPwd(''); setOtpInfo(null); };

  const proceedToPwd = (e) => {
    e?.preventDefault?.();
    if (mobile.length < 6) return toast.error('Enter a valid mobile number');
    setStep(2);
  };

  const requestReset = async (e) => {
    e?.preventDefault?.();
    if (pwd.length < 6) return toast.error('New password must be 6+ chars');
    setLoading(true);
    try {
      const fullMobile = country.code.replace('+', '') + mobile;
      const info = await requestOtp(fullMobile, 'reset');
      setOtpInfo({ ...info, fullMobile });
      setStep(3);
      const chs = (info?.channels || []).map((c) => c === 'whatsapp' ? 'WhatsApp' : c === 'sms' ? 'SMS' : 'Telegram').join(' + ');
      toast.success(chs ? `OTP sent via ${chs}` : 'OTP sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not request OTP');
    } finally { setLoading(false); }
  };

  const verify = async (code) => {
    try {
      await verifyOtp({ mobile: otpInfo.fullMobile, code, purpose: 'reset', password: pwd });
      toast.success('Password reset successful');
      await refresh();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired OTP');
      throw err;
    }
  };

  const close = (v) => { onOpenChange(v); if (!v) reset(); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-[440px] bg-[#1a1a1a] border border-zinc-800 text-zinc-100 p-0 sm:rounded-xl [&>button]:hidden">
        <DialogTitle className="sr-only">Reset Password</DialogTitle>
        <DialogDescription className="sr-only">Reset your password using Telegram OTP</DialogDescription>
        <span className="absolute right-3 top-3 z-20">
          <button onClick={() => close(false)} className="w-8 h-8 rounded-full text-zinc-300 hover:text-white bg-black/30 hover:bg-red-500/80 border border-white/10 flex items-center justify-center transition" data-testid="forgot-close-btn" aria-label="Close password reset">
            <X size={18} />
          </button>
        </span>

        <div className="px-6 pt-6 pb-2 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/30 flex items-center justify-center text-[color:var(--gold)]">
            <KeyRound size={18} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white" data-testid="forgot-title">Reset Password</h2>
            <div className="text-zinc-500 text-xs">Step {step} of 3</div>
          </div>
        </div>

        <div className="px-6 pb-6">
          {step === 1 && (
            <form onSubmit={proceedToPwd} className="space-y-4">
              <div>
                <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">Registered Mobile</label>
                <div className="flex items-stretch bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden focus-within:border-[color:var(--gold)]/50">
                  <CountrySelect value={country} onChange={setCountry} />
                  <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 15))} placeholder="Mobile number" className="flex-1 h-11 bg-transparent px-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none" data-testid="forgot-mobile-input" />
                </div>
              </div>
              <button type="submit" className="w-full h-11 rounded-md bg-[color:var(--gold)] text-black font-bold hover:brightness-110 transition" data-testid="forgot-next-btn">Continue</button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={requestReset} className="space-y-4">
              <div>
                <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">New Password</label>
                <div className="relative">
                  <Input type={showPwd ? 'text' : 'password'} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="At least 6 characters" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100 pr-10" data-testid="forgot-new-password-input" />
                  <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                    {showPwd ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full h-11 rounded-md bg-[color:var(--gold)] text-black font-bold hover:brightness-110 transition disabled:opacity-60" data-testid="forgot-send-otp-btn">
                {loading ? 'Requesting…' : 'Send OTP'}
              </button>
              <button type="button" onClick={() => setStep(1)} className="w-full text-xs text-zinc-400 hover:text-zinc-200">← Back</button>
            </form>
          )}

          {step === 3 && (
            <TelegramOtpStep info={otpInfo} onVerify={verify} onBack={() => setStep(2)} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
